import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useAuth } from "../../context/auth";
import { ADMIN_ROLE, userHasAnyRole } from "../../utils/user";
import { getMaintenanceSettings, type MaintenanceModeState } from "../../services/systemSettings";
import MaintenanceOverlay from "../feature/MaintenanceOverlay";

/**
 * 前台维护模式守卫：
 * - Admin 用户始终放行（正常使用全部功能）；
 * - 非 Admin 用户在前台维护模式开启时，整页替换为维护页（MaintenanceOverlay），
 *   否则正常渲染前台页面。
 *
 * 仅用于包裹前台（ASK / MVR）相关路由，Admin 区域不经过此守卫。
 */
export default function MaintenanceGuard({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const isAdmin = userHasAnyRole(user?.roles, [ADMIN_ROLE]);

  const [maintenance, setMaintenance] = useState<MaintenanceModeState>({
    enabled: false,
    message: null,
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Admin 不需要查询开关状态。
    if (isAdmin) {
      setMaintenance({ enabled: false, message: null });
      setLoaded(true);
      return;
    }

    let cancelled = false;
    getMaintenanceSettings()
      .then((settings) => {
        if (!cancelled) setMaintenance(settings);
      })
      .catch(() => {
        // 查询失败时按关闭处理，避免误伤正常使用。
        if (!cancelled) setMaintenance({ enabled: false, message: null });
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  if (isAdmin) return <>{children}</>;

  // 查询完成前不渲染前台内容，避免维护开启时闪现业务页面。
  if (!loaded) return null;

  if (maintenance.enabled) return <MaintenanceOverlay message={maintenance.message} />;

  return <>{children}</>;
}
